import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Fab,
  Stack,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ViewModule as CardViewIcon,
  TableRows as TableViewIcon,
  PlayArrow,
  PlaylistPlay,
  Stop,
  Close,
  MoreVert,
  Tv,
  Monitor,
  Tablet,
  Lightbulb,
  Wifi,
  WifiOff,
  Error as ErrorIcon,
  Build,
  Settings,
  Info,
  Assignment,
  CameraAlt,
  ControlCamera,
  Delete,
  Update,
  SignalWifi4Bar,
  SignalWifi0Bar,
  LocationOn,
  Memory,
  Storage,
  Computer,
  CalendarMonth
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

// Импорты компонентов
import { 
  DeviceFilters,
  DeviceCard,
  DeviceCreateDialog,
  DeviceEditDialog,
  DeviceDeleteDialog,
  DeviceControlDialog
} from '../../../components/devices';
import DeviceLogsDialog from '../../../components/devices/DeviceLogsDialog';
import DeviceScreenshotsDialog from '../../../components/devices/DeviceScreenshotsDialog';
import DevicePlaybackControls from '../../../components/devices/DevicePlaybackControls';
import DeviceAvailabilityDialog from '../../../components/devices/DeviceAvailabilityDialog';

// Импорты сервисов и типов
import { api } from '../../../services/api';
import { PlaylistStatus, UserRole } from '../../../types';
import type { Device, Playlist, SearchFilters, SortOptions } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { invalidateDeviceData } from '../../../utils/cacheInvalidation';

interface RegisteredDevicesTabProps {
  // Пока без пропов, но может понадобиться для коммуникации между табами
}

export const RegisteredDevicesTab: React.FC<RegisteredDevicesTabProps> = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;

  // Состояние для управления интерфейсом
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    deviceStatuses: [],
    locations: [],
    deviceTypes: [],
    onlineOnly: false,
    offlineOnly: false,
  });
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'name',
    direction: 'asc'
  });

  // Состояние для диалогов
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [controlDialogOpen, setControlDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  
  // Новые диалоги (4 августа 2025)
  const [logsDialog, setLogsDialog] = useState<{ open: boolean; device: Device | null; }>({ open: false, device: null });
  const [screenshotsDialog, setScreenshotsDialog] = useState<{ open: boolean; device: Device | null; }>({ open: false, device: null });
  const [availabilityDialog, setAvailabilityDialog] = useState<{ open: boolean; device: Device | null; }>({ open: false, device: null });
  const [playbackDialog, setPlaybackDialog] = useState<{
    open: boolean;
    device: Device | null;
    initialPlaylistPickerOpen?: boolean;
  }>({ open: false, device: null, initialPlaylistPickerOpen: false });
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<'stop' | 'play_playlist'>('stop');
  const [bulkPlaylistId, setBulkPlaylistId] = useState<number | ''>('');
  const [bulkSyncPlayback, setBulkSyncPlayback] = useState(false);
  const [bulkSyncStartDelaySeconds, setBulkSyncStartDelaySeconds] = useState(10);
  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuDevice, setRowMenuDevice] = useState<Device | null>(null);

  // Запрос устройств
  const {
    data: devicesData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['devices', filters, sortOptions],
    queryFn: () => api.getDevices({
      page: 1,
      size: 100,
      query: filters.search || undefined,
      sort_by: sortOptions.field,
      sort_order: sortOptions.direction,
    }),
    refetchInterval: 30000, // Обновление каждые 30 секунд
  });

  const fetchedDevices = devicesData?.items || [];
  const devices = fetchedDevices.filter((device) => {
    const status = String(device.status || '').toLowerCase();
    const type = String(device.type || device.device_type || '').toLowerCase();
    const location = getDeviceLocation(device);
    const selectedStatuses = (filters.deviceStatuses || []).map((item) => String(item).toLowerCase());
    const selectedTypes = (filters.deviceTypes || []).map((item) => String(item).toLowerCase());

    if (selectedStatuses.length && !selectedStatuses.includes(status)) return false;
    if (selectedTypes.length && !selectedTypes.includes(type)) return false;
    if (filters.locations?.length && !filters.locations.includes(location)) return false;
    if (filters.onlineOnly && status !== 'online') return false;
    if (filters.offlineOnly && status !== 'offline') return false;
    return true;
  });
  const totalDevices = devices.length;
  const availableLocations = Array.from(
    new Set(fetchedDevices.map((device) => getDeviceLocation(device)).filter((location) => location !== '—'))
  ).sort((a, b) => a.localeCompare(b, 'ru'));
  const visibleDeviceIds = devices.map((device) => device.id);
  const visibleDeviceIdsKey = visibleDeviceIds.join(',');
  const allVisibleSelected = visibleDeviceIds.length > 0 && visibleDeviceIds.every((id) => selectedDeviceIds.includes(id));

  const { data: playlistsData, isLoading: playlistsLoading } = useQuery({
    queryKey: ['bulk-device-action-playlists'],
    queryFn: () => api.getPlaylists(undefined, undefined, 1, 100),
    enabled: isAdmin,
    staleTime: 30000,
  });

  const activePlaylists = (playlistsData?.items || []).filter(
    (playlist: Playlist) => playlist.status === PlaylistStatus.ACTIVE
  );

  const selectedBulkPlaylist = activePlaylists.find((playlist) => playlist.id === bulkPlaylistId) || null;

  const { data: syncSessionsData } = useQuery({
    queryKey: ['playback-sync-sessions-devices-tab'],
    queryFn: () => api.getPlaybackSyncSessions({ page: 1, size: 50 }),
    refetchInterval: 10000,
    enabled: isAdmin,
  });

  const syncByDeviceId = new Map<number, { sessionId: number; status: string; drift?: number | null; playlistName?: string | null }>();
  (syncSessionsData?.items || [])
    .filter((session) => ['scheduled', 'starting', 'active'].includes(session.status))
    .forEach((session) => {
      session.devices.forEach((row) => {
        syncByDeviceId.set(row.device_id, {
          sessionId: session.id,
          status: row.status,
          drift: row.drift_ms,
          playlistName: session.playlist_name,
        });
      });
    });

  useEffect(() => {
    setSelectedDeviceIds((current) => current.filter((id) => visibleDeviceIds.includes(id)));
  }, [visibleDeviceIdsKey]);

  const [openedDeviceFromQuery, setOpenedDeviceFromQuery] = useState<number | null>(null);

  useEffect(() => {
    const deviceId = Number(new URLSearchParams(window.location.search).get('device'));
    if (!deviceId || openedDeviceFromQuery === deviceId || fetchedDevices.length === 0) {
      return;
    }

    const device = fetchedDevices.find((item) => item.id === deviceId);
    if (device) {
      setOpenedDeviceFromQuery(deviceId);
      handleEditDevice(device);
    }
  }, [fetchedDevices, openedDeviceFromQuery]);

  const bulkCommandMutation = useMutation({
    mutationFn: async () => {
      if (selectedDeviceIds.length === 0) {
        throw new Error('Выберите хотя бы одно устройство');
      }

      if (bulkAction === 'play_playlist') {
        if (!selectedBulkPlaylist) {
          throw new Error('Выберите плейлист для запуска');
        }

        if (bulkSyncPlayback) {
          return api.createPlaybackSyncSession({
            playlist_id: selectedBulkPlaylist.id,
            device_ids: selectedDeviceIds,
            start_delay_seconds: bulkSyncStartDelaySeconds,
          });
        }

        return api.sendBulkDeviceCommands({
          device_ids: selectedDeviceIds,
          command_type: 'play_playlist',
          command_data: {
            playlist_id: selectedBulkPlaylist.id,
            name: selectedBulkPlaylist.name,
            force: true,
            source: 'admin_bulk_action',
          },
        });
      }

      return api.sendBulkDeviceCommands({
        device_ids: selectedDeviceIds,
        command_type: 'stop',
        command_data: {
          force: true,
          source: 'admin_bulk_action',
        },
      });
    },
    onSuccess: async (result) => {
      await invalidateDeviceData(queryClient);
      const count = result.devices_count ?? result.commands_sent ?? result.devices?.length ?? selectedDeviceIds.length;
      const message = bulkAction === 'play_playlist'
        ? bulkSyncPlayback && result.start_at
          ? `Синхронный запуск "${selectedBulkPlaylist?.name}" запланирован на ${new Date(result.start_at).toLocaleTimeString()} для ${count} устройств`
          : `Команда запуска плейлиста "${selectedBulkPlaylist?.name}" отправлена на ${count} устройств`
        : `Команда остановки отправлена на ${count} устройств`;
      enqueueSnackbar(message, { variant: 'success' });
      setSelectedDeviceIds([]);
    },
    onError: (error: any) => {
      const message = error?.detail || error?.message || String(error);
      enqueueSnackbar(message, { variant: 'error' });
    },
  });

  // Обработчики для диалогов
  const handleCreateDevice = () => {
    setCreateDialogOpen(true);
  };

  const handleEditDevice = (device: Device) => {
    setSelectedDevice(device);
    setEditDialogOpen(true);
  };

  const handleDeleteDevice = (device: Device) => {
    setSelectedDevice(device);
    setDeleteDialogOpen(true);
  };

  const handleControlDevice = (device: Device) => {
    setSelectedDevice(device);
    setControlDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setControlDialogOpen(false);
    setSelectedDevice(null);
  };

  // Обработчик успешных операций
  const handleSuccess = (message: string) => {
    enqueueSnackbar(message, { variant: 'success' });
    invalidateDeviceData(queryClient);
    handleCloseDialogs();
  };

  // Обработчик обновления фильтров
  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      deviceStatuses: [],
      locations: [],
      deviceTypes: [],
      onlineOnly: false,
      offlineOnly: false,
    });
  };

  // Обработчик изменения сортировки
  const handleSortChange = (newSortOptions: SortOptions) => {
    setSortOptions(newSortOptions);
  };

  // Новые обработчики диалогов (4 августа 2025)
  const handleViewLogs = (device: Device) => {
    setLogsDialog({ open: true, device });
  };

  const handleViewScreenshots = (device: Device) => {
    setScreenshotsDialog({ open: true, device });
  };

  const handleViewAvailability = (device: Device) => {
    setAvailabilityDialog({ open: true, device });
  };

  const handlePlaybackControls = (device: Device, initialPlaylistPickerOpen = false) => {
    setPlaybackDialog({ open: true, device, initialPlaylistPickerOpen });
  };

  const handleToggleDeviceSelection = (deviceId: number) => {
    setSelectedDeviceIds((current) =>
      current.includes(deviceId)
        ? current.filter((id) => id !== deviceId)
        : [...current, deviceId]
    );
  };

  const handleToggleAllVisibleDevices = () => {
    setSelectedDeviceIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleDeviceIds.includes(id));
      }
      return Array.from(new Set([...current, ...visibleDeviceIds]));
    });
  };

  const handleOpenRowMenu = (event: React.MouseEvent<HTMLElement>, device: Device) => {
    setRowMenuAnchor(event.currentTarget);
    setRowMenuDevice(device);
  };

  const handleCloseRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuDevice(null);
  };

  const handleRowMenuAction = (action: (device: Device) => void) => {
    if (rowMenuDevice) {
      action(rowMenuDevice);
    }
    handleCloseRowMenu();
  };

  const deviceTypeLabels: Record<string, string> = {
    tv: 'Телевизор',
    monitor: 'Монитор',
    tablet: 'Планшет',
    led_panel: 'LED панель',
    video_wall: 'Видеостена',
    desktop_pc: 'ПК',
    raspberry_pi: 'Raspberry Pi',
    intel_nuc: 'Intel NUC',
    android_tv: 'Android TV',
    web_browser: 'Web Player',
    other: 'Другое',
  };

  const getDeviceTypeIconByType = (type?: string) => {
    switch (type) {
      case 'tv':
        return Tv;
      case 'tablet':
        return Tablet;
      case 'led_panel':
        return Lightbulb;
      case 'video_wall':
        return CardViewIcon;
      case 'desktop_pc':
      case 'intel_nuc':
      case 'raspberry_pi':
        return Computer;
      default:
        return Monitor;
    }
  };

  const inferPhysicalDeviceType = (device: Device, systemInfo: Record<string, any>) => {
    const explicitType = String(device.device_type || device.type || '').toLowerCase();
    if (explicitType && explicitType !== 'web_browser' && explicitType !== 'other' && explicitType !== 'unknown') {
      return explicitType;
    }

    const name = `${device.name || ''} ${device.hostname || ''} ${device.location || ''} ${device.room || ''}`.toLowerCase();
    const osName = String(systemInfo.os_name || device.os_version || '').toLowerCase();
    const platform = String(systemInfo.platform || '').toLowerCase();
    const userAgent = String(systemInfo.user_agent || '').toLowerCase();

    if (/(^|\b)(tv|телевизор|телевиз|screen|экран|display)(\b|$)/i.test(name)) return 'tv';
    if (name.includes('tablet') || name.includes('планш')) return 'tablet';
    if (name.includes('led')) return 'led_panel';
    if (name.includes('video wall') || name.includes('видеостен')) return 'video_wall';
    if (osName.includes('android') || userAgent.includes('android')) return 'android_tv';
    if (
      osName.includes('windows') ||
      osName.includes('mac') ||
      osName.includes('linux') ||
      platform.includes('win') ||
      platform.includes('mac') ||
      platform.includes('linux')
    ) {
      return 'desktop_pc';
    }
    return 'monitor';
  };

  const getDevicePresentation = (device: Device, systemInfo: Record<string, any>) => {
    const physicalType = inferPhysicalDeviceType(device, systemInfo);
    return {
      type: physicalType,
      label: deviceTypeLabels[physicalType] || deviceTypeLabels.monitor,
      Icon: getDeviceTypeIconByType(physicalType),
      inferred: String(device.device_type || device.type || '').toLowerCase() === 'web_browser',
    };
  };

  const getClientMeta = (device: Device, systemInfo: Record<string, any>) => {
    const deviceType = String(device.device_type || device.type || '').toLowerCase();
    const player = String(systemInfo.player || systemInfo.player_kind || systemInfo.client_kind || '').toLowerCase();
    const cacheSource = String(systemInfo.cache_status?.source || '').toLowerCase();
    const userAgent = String(systemInfo.user_agent || '').toLowerCase();
    const osName = String(systemInfo.os_name || device.os_version || '').toLowerCase();

    if (systemInfo.client_label) {
      return { label: String(systemInfo.client_label), color: 'info' as const, kind: String(systemInfo.client_kind || 'client') };
    }
    if (player.includes('native-android') || player.includes('native_android') || player.includes('android_apk') || cacheSource.includes('android')) {
      return { label: 'Android APK', color: 'success' as const, kind: 'android_apk' };
    }
    if (player.includes('desktop-python') || player.includes('desktop_python') || cacheSource.includes('desktop-python')) {
      return { label: 'Desktop Python', color: 'primary' as const, kind: 'desktop_python' };
    }
    if (player.includes('electron') || userAgent.includes('electron')) {
      return { label: 'Windows Electron', color: 'info' as const, kind: 'electron_web_player' };
    }
    if (deviceType === 'web_browser' || player.includes('web-player') || player.includes('web_browser')) {
      return { label: osName.includes('windows') ? 'Web/Electron' : 'Browser Web', color: 'info' as const, kind: 'browser_web_player' };
    }
    return { label: 'Device API', color: 'default' as const, kind: 'device_api' };
  };

  const isBrowserRuntime = (device: Device, systemInfo: Record<string, any>) => {
    const deviceType = String(device.device_type || device.type || '').toLowerCase();
    const player = String(systemInfo.player || systemInfo.player_kind || systemInfo.client_kind || '').toLowerCase();
    return deviceType === 'web_browser' || player.includes('web') || Boolean(systemInfo.browser_name || systemInfo.user_agent);
  };

  const getStatusMeta = (status: Device['status']) => {
    switch (status) {
      case 'online':
        return { color: 'success' as const, icon: <Wifi fontSize="small" />, label: t('devices.status.online') };
      case 'offline':
        return { color: 'error' as const, icon: <WifiOff fontSize="small" />, label: t('devices.status.offline') };
      case 'maintenance':
        return { color: 'warning' as const, icon: <Build fontSize="small" />, label: t('devices.status.maintenance') };
      case 'error':
        return { color: 'error' as const, icon: <ErrorIcon fontSize="small" />, label: t('devices.status.error') };
      default:
        return { color: 'default' as const, icon: <WifiOff fontSize="small" />, label: String(status || 'unknown') };
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelative = (value?: string | null) => {
    if (!value) return 'нет данных';
    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return 'нет данных';
    const diffMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
    if (diffMinutes < 1) return 'только что';
    if (diffMinutes < 60) return `${diffMinutes} мин назад`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} ч назад`;
    return `${Math.floor(diffHours / 24)} д назад`;
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds) return '—';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days} д ${hours} ч`;
    if (hours > 0) return `${hours} ч ${minutes} мин`;
    return `${minutes} мин`;
  };

  const getSignalIcon = (device: Device) => {
    if (getDisplayIp(device) && device.status === 'online') {
      return SignalWifi4Bar;
    }
    const signal = device.signal_strength ?? 0;
    return signal >= 25 ? SignalWifi4Bar : SignalWifi0Bar;
  };

  const getDisplayIp = (device: Device) => device.ip_address || device.last_ip_address || '';

  const getSystemInfo = (device: Device) => ({
    ...(device.device_settings || {}),
    ...(device.system_info || {}),
  });

  const formatBytes = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
  };

  const getCacheStatus = (systemInfo: Record<string, any>) => {
    const cacheStatus = systemInfo.cache_status || systemInfo.cache;
    if (!cacheStatus || typeof cacheStatus !== 'object') return null;
    const contentStats = cacheStatus.content_stats || {};
    const mediaStats = contentStats.media || contentStats.video || {};
    const items = Array.isArray(cacheStatus.items) ? cacheStatus.items : [];
    const cachedMediaIds = Array.isArray(cacheStatus.cached_media_ids) ? cacheStatus.cached_media_ids : [];
    const mediaEntries = Number(
      cacheStatus.media_entries ??
      cacheStatus.total_files ??
      mediaStats.count ??
      cachedMediaIds.length ??
      items.length ??
      0
    );
    const totalEntries = Number(cacheStatus.total_entries ?? mediaEntries ?? items.length ?? 0);
    const totalSizeBytes = Number(
      cacheStatus.total_size_bytes ??
      cacheStatus.total_size ??
      mediaStats.size ??
      items.reduce((sum: number, item: any) => sum + Number(item.size_bytes ?? item.size ?? 0), 0)
    );

    return {
      ...cacheStatus,
      items,
      mediaEntries: Number.isFinite(mediaEntries) ? mediaEntries : 0,
      totalEntries: Number.isFinite(totalEntries) ? totalEntries : 0,
      totalSizeBytes: Number.isFinite(totalSizeBytes) ? totalSizeBytes : 0,
    };
  };

  const getCacheTooltip = (cacheStatus: ReturnType<typeof getCacheStatus>) => {
    if (!cacheStatus) return 'Плеер еще не передал статус кеша';
    const lines = [
      `Источник: ${cacheStatus.source || 'unknown'}`,
      `Файлов медиа: ${cacheStatus.mediaEntries}`,
      `Объем: ${formatBytes(cacheStatus.totalSizeBytes)}`,
      cacheStatus.updated_at ? `Обновлено: ${formatDateTime(cacheStatus.updated_at)}` : '',
    ].filter(Boolean);
    const itemLines = cacheStatus.items.slice(0, 8).map((item: any) => {
      const title = item.filename || (item.media_id ? `media #${item.media_id}` : 'cached item');
      const size = formatBytes(item.size_bytes ?? item.size);
      return `${item.media_id ? `#${item.media_id} ` : ''}${title} (${size})`;
    });
    if (itemLines.length) {
      lines.push('', ...itemLines);
    }
    if (cacheStatus.error) {
      lines.push('', `Ошибка: ${cacheStatus.error}`);
    }
    return lines.join('\n');
  };

  const getBrowserName = (userAgent?: string) => {
    if (!userAgent) return 'Browser';
    if (userAgent.includes('Edg/')) return 'Edge';
    if (userAgent.includes('Chrome/')) return 'Chrome';
    if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari';
    if (userAgent.includes('Firefox/')) return 'Firefox';
    return 'Browser';
  };

  const getBrowserLabel = (systemInfo: Record<string, any>) => {
    if (systemInfo.browser_name) {
      return [systemInfo.browser_name, systemInfo.browser_version].filter(Boolean).join(' ');
    }
    return getBrowserName(systemInfo.user_agent);
  };

  const getOsLabel = (device: Device, systemInfo: Record<string, any>) => {
    if (systemInfo.os_name) {
      return [systemInfo.os_name, systemInfo.os_version].filter(Boolean).join(' ');
    }
    return device.os_version || 'OS —';
  };

  const getPlaybackStatusMeta = (status?: string) => {
    switch (String(status || '').toLowerCase()) {
      case 'playing':
        return { label: 'playing', color: 'primary' as const };
      case 'paused':
        return { label: 'paused', color: 'warning' as const };
      case 'loading':
        return { label: 'loading', color: 'info' as const };
      case 'error':
        return { label: 'playback error', color: 'error' as const };
      case 'stopped':
        return { label: 'stopped', color: 'default' as const };
      default:
        return null;
    }
  };

  function getDeviceLocation(device: Device) {
    const parts = [device.location, device.floor, device.room].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  }

  const getPlaybackLabel = (device: Device) => {
    const playbackStatus = (device as any).playback_status;
    if (device.current_media?.title) {
      return device.current_media.title;
    }
    if (device.current_playlist?.name) {
      return device.current_playlist.name;
    }
    if ((device as any).current_playlist_id) {
      return `Playlist #${(device as any).current_playlist_id}`;
    }
    if ((device as any).current_media_id) {
      return `Media #${(device as any).current_media_id}`;
    }
    if (playbackStatus) {
      return String(playbackStatus);
    }
    return '—';
  };

  // Получение статистики устройств
  const deviceStats = {
    total: totalDevices,
    online: devices.filter(d => d.status === 'online').length,
    offline: devices.filter(d => d.status === 'offline').length,
    maintenance: devices.filter(d => d.status === 'maintenance').length,
    error: devices.filter(d => d.status === 'error').length,
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок и статистика */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {t('devices.management.tabs.devices')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('devices.management.devicesDescription')}
            </Typography>
          </Box>

          {/* Кнопки управления */}
          <Stack direction="row" spacing={1}>
            <Tooltip title={t('common.refresh')}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => refetch()}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {t('common.refresh')}
              </Button>
            </Tooltip>

            <Tooltip title={viewMode === 'cards' ? t('common.tableView') : t('common.cardView')}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
                startIcon={viewMode === 'cards' ? <TableViewIcon /> : <CardViewIcon />}
              >
                {viewMode === 'cards' ? t('common.tableView') : t('common.cardView')}
              </Button>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDevice}
              sx={{ ml: 2 }}
            >
              {t('devices.addDevice')}
            </Button>
          </Stack>
        </Box>

        {/* Статистика устройств */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Chip 
            label={`${t('devices.totalDevices')}: ${deviceStats.total}`}
            color="default"
            variant="outlined"
          />
          <Chip 
            label={`🟢 ${t('devices.status.online')}: ${deviceStats.online}`}
            color="success"
            variant="filled"
          />
          <Chip 
            label={`🔴 ${t('devices.status.offline')}: ${deviceStats.offline}`}
            color="error"
            variant="filled"
          />
          {deviceStats.maintenance > 0 && (
            <Chip 
              label={`🟡 ${t('devices.status.maintenance')}: ${deviceStats.maintenance}`}
              color="warning"
              variant="filled"
            />
          )}
          {deviceStats.error > 0 && (
            <Chip 
              label={`❌ ${t('devices.status.error')}: ${deviceStats.error}`}
              color="error"
              variant="outlined"
            />
          )}
        </Stack>
      </Box>

      {/* Фильтры */}
      <DeviceFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        sortOptions={sortOptions}
        onSortChange={handleSortChange}
        onClearFilters={handleClearFilters}
        totalItems={totalDevices}
        onlineItems={deviceStats.online}
        offlineItems={deviceStats.offline}
        availableLocations={availableLocations}
      />

      {isAdmin && (
        <Paper sx={{ p: 2, mt: 3, border: 1, borderColor: 'divider' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 240 }}>
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && selectedDeviceIds.length > 0}
                onChange={handleToggleAllVisibleDevices}
                disabled={devices.length === 0 || bulkCommandMutation.isPending}
              />
              <Box>
                <Typography variant="subtitle2">Групповые действия</Typography>
                <Typography variant="caption" color="text.secondary">
                  Выбрано: {selectedDeviceIds.length} из {devices.length} показанных
                </Typography>
              </Box>
            </Box>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Действие</InputLabel>
              <Select
                value={bulkAction}
                label="Действие"
                onChange={(event) => setBulkAction(event.target.value as 'stop' | 'play_playlist')}
                disabled={bulkCommandMutation.isPending}
              >
                <MenuItem value="stop">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Stop fontSize="small" />
                    Остановить
                  </Box>
                </MenuItem>
                <MenuItem value="play_playlist">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PlaylistPlay fontSize="small" />
                    Запустить плейлист
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {bulkAction === 'play_playlist' && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 320 } }}>
                <InputLabel>Плейлист</InputLabel>
                <Select
                  value={bulkPlaylistId}
                  label="Плейлист"
                  onChange={(event) => setBulkPlaylistId(Number(event.target.value))}
                  disabled={playlistsLoading || bulkCommandMutation.isPending}
                >
                  {activePlaylists.map((playlist) => (
                    <MenuItem key={playlist.id} value={playlist.id}>
                      {playlist.name} ({playlist.items_count || 0})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {bulkAction === 'play_playlist' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bulkSyncPlayback}
                      onChange={(event) => setBulkSyncPlayback(event.target.checked)}
                      disabled={bulkCommandMutation.isPending}
                    />
                  }
                  label="Синхронно"
                />
                {bulkSyncPlayback && (
                  <TextField
                    size="small"
                    type="number"
                    label="Старт через, сек"
                    value={bulkSyncStartDelaySeconds}
                    onChange={(event) => setBulkSyncStartDelaySeconds(Math.max(3, Number(event.target.value) || 10))}
                    inputProps={{ min: 3, max: 300 }}
                    disabled={bulkCommandMutation.isPending}
                    sx={{ width: 150 }}
                  />
                )}
              </Stack>
            )}

            <Button
              variant="contained"
              color={bulkAction === 'stop' ? 'error' : 'primary'}
              startIcon={bulkCommandMutation.isPending ? <CircularProgress size={16} /> : (bulkAction === 'stop' ? <Stop /> : <PlaylistPlay />)}
              disabled={
                bulkCommandMutation.isPending ||
                selectedDeviceIds.length === 0 ||
                (bulkAction === 'play_playlist' && !selectedBulkPlaylist)
              }
              onClick={() => bulkCommandMutation.mutate()}
            >
              Выполнить
            </Button>

            {selectedDeviceIds.length > 0 && (
              <Button
                variant="text"
                onClick={() => setSelectedDeviceIds([])}
                disabled={bulkCommandMutation.isPending}
              >
                Сбросить выбор
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* Контент */}
      <Box sx={{ mt: 3 }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('devices.errorLoadingDevices')}: {error?.message}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {filters.search || filters.deviceStatuses?.length || filters.locations?.length || filters.deviceTypes?.length || filters.onlineOnly || filters.offlineOnly
                ? t('devices.noDevicesFound')
                : t('devices.noDevices')
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('devices.noDevicesDescription')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateDevice}
            >
              {t('devices.addFirstDevice')}
            </Button>
          </Box>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <Grid container spacing={3}>
                {devices.map((device) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={device.id}>
                    <Box sx={{ position: 'relative' }}>
                      {isAdmin && (
                        <Checkbox
                          checked={selectedDeviceIds.includes(device.id)}
                          onChange={() => handleToggleDeviceSelection(device.id)}
                          disabled={bulkCommandMutation.isPending}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'background.paper' },
                          }}
                        />
                      )}
                      <DeviceCard
                        device={device}
                        onEdit={() => handleEditDevice(device)}
                        onDelete={() => handleDeleteDevice(device)}
                        onViewDetails={() => handleEditDevice(device)}
                        onRestart={() => handleControlDevice(device)}
                        onUpdate={() => handleControlDevice(device)}
                        onViewLogs={() => handleViewLogs(device)}
                        onViewScreenshots={() => handleViewScreenshots(device)}
                        onViewAvailability={() => handleViewAvailability(device)}
                        onPlaybackControls={() => handlePlaybackControls(device)}
                        onChangePlaylist={() => handlePlaybackControls(device, true)}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  maxWidth: '100%',
                  overflowX: 'hidden',
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    '& th, & td': {
                      px: 0.75,
                      py: 0.75,
                      verticalAlign: 'top',
                      overflow: 'hidden',
                    },
                    '& .MuiChip-root': {
                      maxWidth: '100%',
                    },
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {isAdmin && (
                        <TableCell padding="checkbox" sx={{ width: 42 }}>
                          <Checkbox
                            checked={allVisibleSelected}
                            indeterminate={!allVisibleSelected && selectedDeviceIds.length > 0}
                            onChange={handleToggleAllVisibleDevices}
                            disabled={bulkCommandMutation.isPending}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ width: '23%' }}>Устройство</TableCell>
                      <TableCell sx={{ width: '14%' }}>Статус</TableCell>
                      <TableCell sx={{ width: '21%' }}>Воспроизведение</TableCell>
                      <TableCell sx={{ width: '25%' }}>Техника / сеть / локация</TableCell>
                      <TableCell sx={{ width: '11%' }}>Активность</TableCell>
                      <TableCell align="right" sx={{ width: 116 }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device) => {
                      const statusMeta = getStatusMeta(device.status);
                      const SignalIcon = getSignalIcon(device);
                      const playbackStatus = String((device as any).playback_status || '').toLowerCase();
                      const playbackStatusMeta = getPlaybackStatusMeta(playbackStatus);
                      const displayIp = getDisplayIp(device);
                      const systemInfo = getSystemInfo(device);
                      const cacheStatus = getCacheStatus(systemInfo);
                      const devicePresentation = getDevicePresentation(device, systemInfo);
                      const DeviceTypeIcon = devicePresentation.Icon;
                      const clientMeta = getClientMeta(device, systemInfo);
                      const isWebPlayer = isBrowserRuntime(device, systemInfo);
                      const syncState = syncByDeviceId.get(device.id);

                      return (
                        <TableRow
                          key={device.id}
                          hover
                          selected={selectedDeviceIds.includes(device.id)}
                          sx={{
                            '& .row-actions': { opacity: 0.72 },
                            '&:hover .row-actions': { opacity: 1 },
                          }}
                        >
                          {isAdmin && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDeviceIds.includes(device.id)}
                                onChange={() => handleToggleDeviceSelection(device.id)}
                                disabled={bulkCommandMutation.isPending}
                              />
                            </TableCell>
                          )}

                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  flex: '0 0 auto',
                                  bgcolor: device.status === 'online' ? 'success.main' : 'grey.500',
                                }}
                              >
                                <DeviceTypeIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={device.name}
                                >
                                  {device.name}
                                </Typography>
                                <Stack direction="row" spacing={0.5} useFlexGap alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={devicePresentation.label}
                                    size="small"
                                    variant="outlined"
                                    color={devicePresentation.inferred ? 'info' : 'default'}
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                  <Chip
                                    label={clientMeta.label}
                                    size="small"
                                    variant="outlined"
                                    color={clientMeta.color}
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                  {device.maintenance_mode && (
                                    <Chip label="maintenance" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                                  )}
                                  {device.is_active === false && (
                                    <Chip label="inactive" size="small" color="default" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                                  )}
                                </Stack>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={`ID ${device.id}${device.hostname ? ` • ${device.hostname}` : ''}`}
                                >
                                  ID {device.id}{device.hostname ? ` • ${device.hostname}` : ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                              <Chip
                                icon={statusMeta.icon}
                                label={statusMeta.label}
                                color={statusMeta.color}
                                size="small"
                                variant="filled"
                                sx={{ height: 22, fontSize: 11 }}
                              />
                              {playbackStatusMeta && (
                                <Chip
                                  label={playbackStatusMeta.label}
                                  color={playbackStatusMeta.color}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              )}
                              {syncState && (
                                <Tooltip title={`${syncState.playlistName || 'Sync playlist'} • session #${syncState.sessionId}`}>
                                  <Chip
                                    label={`SYNC ${syncState.drift !== null && syncState.drift !== undefined ? `${syncState.drift}ms` : syncState.status}`}
                                    color={syncState.status === 'drift' || syncState.status === 'failed' ? 'warning' : 'info'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                                <PlaylistPlay fontSize="small" color="action" />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.25,
                                  }}
                                  title={getPlaybackLabel(device)}
                                >
                                  {getPlaybackLabel(device)}
                                </Typography>
                              </Box>
                              {device.current_playlist?.id && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  Playlist ID: {device.current_playlist.id}
                                </Typography>
                              )}
                              {device.current_media && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={device.current_media.filename}
                                >
                                  Media ID: {device.current_media.id} • {device.current_media.media_type}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={0.5} useFlexGap alignItems="center" sx={{ flexWrap: 'wrap', minWidth: 0 }}>
                                {isWebPlayer && (
                                  <Chip
                                    icon={<Computer fontSize="small" />}
                                    label={getBrowserLabel(systemInfo)}
                                    size="small"
                                    color={systemInfo.online === false ? 'warning' : 'success'}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                                {isWebPlayer && systemInfo.hardware_concurrency !== undefined && (
                                  <Chip
                                    icon={<Memory fontSize="small" />}
                                    label={`CPU cores ${systemInfo.hardware_concurrency}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                                {isWebPlayer && systemInfo.device_memory_gb !== undefined && (
                                  <Chip
                                    icon={<Memory fontSize="small" />}
                                    label={`RAM ~${systemInfo.device_memory_gb} GB`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                                {isWebPlayer && systemInfo.connection_type && (
                                  <Chip
                                    icon={<SignalWifi4Bar fontSize="small" />}
                                    label={`${systemInfo.connection_type}${systemInfo.connection_downlink_mbps ? ` ${systemInfo.connection_downlink_mbps} Mbps` : ''}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                                {systemInfo.cpu_usage !== undefined && (
                                  <Chip
                                    icon={<Memory fontSize="small" />}
                                    label={`CPU ${systemInfo.cpu_usage}%`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                                {systemInfo.memory_usage !== undefined && (
                                  <Chip
                                    icon={<Memory fontSize="small" />}
                                    label={`RAM ${systemInfo.memory_usage}%`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                                {systemInfo.storage_usage !== undefined && (
                                  <Chip
                                    icon={<Storage fontSize="small" />}
                                    label={`Disk ${systemInfo.storage_usage}%`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                )}
                              </Stack>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                <SignalIcon fontSize="small" color="action" />
                                <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {displayIp || '—'}{device.mac_address ? ` • ${device.mac_address}` : ''}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                <LocationOn fontSize="small" color="action" />
                                <Typography
                                  variant="caption"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={getDeviceLocation(device)}
                                >
                                  {getDeviceLocation(device)}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={`${getOsLabel(device, systemInfo)}${device.player_version ? ` • Player ${device.player_version}` : ''}`}
                              >
                                {getOsLabel(device, systemInfo)}{device.player_version ? ` • Player ${device.player_version}` : ''}
                              </Typography>
                              {device.screen_resolution && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {device.screen_resolution} • {device.screen_orientation || 'landscape'}
                                  {systemInfo.device_pixel_ratio ? ` • DPR ${systemInfo.device_pixel_ratio}` : ''}
                                </Typography>
                              )}
                              <Tooltip
                                title={<Box component="pre" sx={{ m: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>{getCacheTooltip(cacheStatus)}</Box>}
                                arrow
                              >
                                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', width: 'fit-content' }}>
                                  <Chip
                                    icon={<Storage fontSize="small" />}
                                    label={cacheStatus ? `Кеш: ${cacheStatus.mediaEntries}` : 'Кеш: нет данных'}
                                    color={cacheStatus?.error ? 'warning' : cacheStatus ? 'success' : 'default'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                  {cacheStatus && (
                                    <Chip
                                      label={formatBytes(cacheStatus.totalSizeBytes)}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: 11 }}
                                    />
                                  )}
                                </Stack>
                              </Tooltip>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{formatRelative(device.last_heartbeat)}</Typography>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.2 }}>
                                {formatDateTime(device.last_heartbeat)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.2 }}>
                                Uptime: {formatUptime(device.uptime)}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              className="row-actions"
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="center"
                              useFlexGap
                              sx={{ flexWrap: 'wrap' }}
                            >
                              <Tooltip title={t('common.details')}>
                                <IconButton size="small" onClick={() => handleEditDevice(device)}>
                                  <Info fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('devices.playback.title')}>
                                <IconButton size="small" onClick={() => handlePlaybackControls(device)}>
                                  <ControlCamera fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Запустить плейлист">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePlaybackControls(device, true)}
                                    disabled={device.status !== 'online'}
                                  >
                                    <PlaylistPlay fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t('devices.logs.viewLogs')}>
                                <IconButton size="small" onClick={() => handleViewLogs(device)}>
                                  <Assignment fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Календарь доступности">
                                <IconButton size="small" onClick={() => handleViewAvailability(device)}>
                                  <CalendarMonth fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Еще">
                                <IconButton size="small" onClick={(event) => handleOpenRowMenu(event, device)}>
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleRowMenuAction(handleEditDevice)}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleRowMenuAction(handleControlDevice)}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('devices.restart')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleRowMenuAction(handleControlDevice)}>
          <ListItemIcon>
            <Update fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('devices.commands.update')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleRowMenuAction(handleViewScreenshots)}>
          <ListItemIcon>
            <CameraAlt fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('devices.screenshots.title')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleRowMenuAction(handleViewAvailability)}>
          <ListItemIcon>
            <CalendarMonth fontSize="small" />
          </ListItemIcon>
          <ListItemText>Календарь доступности</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleRowMenuAction(handleDeleteDevice)}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Floating Action Button для быстрого добавления */}
      <Fab
        color="primary"
        aria-label={t('devices.addDevice')}
        onClick={handleCreateDevice}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <AddIcon />
      </Fab>

      {/* Диалоги */}
      <DeviceCreateDialog
        open={createDialogOpen}
        onClose={handleCloseDialogs}
        onSuccess={(message) => handleSuccess(message)}
      />

      {selectedDevice && (
        <>
          <DeviceEditDialog
            open={editDialogOpen}
            device={selectedDevice}
            onClose={handleCloseDialogs}
            onSuccess={(message) => handleSuccess(message)}
          />

          <DeviceDeleteDialog
            open={deleteDialogOpen}
            device={selectedDevice}
            onClose={handleCloseDialogs}
            onSuccess={(message) => handleSuccess(message)}
          />

          <DeviceControlDialog
            open={controlDialogOpen}
            device={selectedDevice}
            onClose={handleCloseDialogs}
          />
        </>
      )}

      {/* Новые диалоги (4 августа 2025) */}
      <DeviceLogsDialog
        open={logsDialog.open}
        onClose={() => setLogsDialog({ open: false, device: null })}
        device={logsDialog.device}
      />

      <DeviceScreenshotsDialog
        open={screenshotsDialog.open}
        onClose={() => setScreenshotsDialog({ open: false, device: null })}
        device={screenshotsDialog.device}
      />

      <DeviceAvailabilityDialog
        open={availabilityDialog.open}
        onClose={() => setAvailabilityDialog({ open: false, device: null })}
        device={availabilityDialog.device}
      />

      {playbackDialog.open && playbackDialog.device && (
        <Dialog 
          open={playbackDialog.open} 
          onClose={() => setPlaybackDialog({ open: false, device: null, initialPlaylistPickerOpen: false })} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PlayArrow color="primary" />
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {t('devices.playback.title')} - {playbackDialog.device.name}
              </Typography>
              <IconButton 
                onClick={() => setPlaybackDialog({ open: false, device: null, initialPlaylistPickerOpen: false })} 
                size="small"
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DevicePlaybackControls
              device={playbackDialog.device}
              initialPlaylistPickerOpen={playbackDialog.initialPlaylistPickerOpen}
              onSuccess={(message) => enqueueSnackbar(message, { variant: 'success' })}
              onError={(error) => enqueueSnackbar(error, { variant: 'error' })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlaybackDialog({ open: false, device: null, initialPlaylistPickerOpen: false })}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}; 
